import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Scale, Loader2 } from 'lucide-react';
import { useScaleStore } from '@/store/scaleStore';
import { useScaleConnection } from '@/hooks/useScaleConnection';
import {
  SCALE_BRAND_PRESETS,
  SCALE_PROTOCOL_OPTIONS,
  type ScaleBrandPreset,
  type ScaleProtocol,
} from '@/utils/scaleProtocols';
import { isDesktopApp, getElectronAPI } from '@/desktop/electronApi';

const SELECT_CLS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900';

export default function ScaleSettingsPage() {
  const { t } = useTranslation();
  const {
    draft,
    isDirty,
    errorMsg,
    lastWeightKg,
    updateDraft,
    applyBrandPreset,
    saveDraft,
    discardDraft,
    resetDefaults,
    setLastWeightKg,
  } = useScaleStore();

  const {
    status: connStatus,
    listPorts,
    requestWebPort,
    testConnection,
    disconnect,
    transport,
  } = useScaleConnection();

  const [ports, setPorts] = useState<Array<{ id: string; label: string; path?: string }>>([]);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [testing, setTesting] = useState(false);
  const desktop = isDesktopApp();

  useEffect(() => {
    void listPorts().then(setPorts);
  }, [listPorts]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = async () => {
    saveDraft();
    if (desktop) {
      try {
        await getElectronAPI()?.setConfig?.({
          scalePath: draft.path,
          scaleBaudRate: draft.baudRate,
          scaleProtocol: draft.protocol,
        } as never);
      } catch {
        /* optional desktop config mirror */
      }
    }
    showToast(t('settings.scaleSaved', { defaultValue: 'Scale settings saved.' }), true);
  };

  const handleTest = async () => {
    setTesting(true);
    setLastWeightKg(null);
    const res = await testConnection();
    setTesting(false);
    showToast(
      res.ok
        ? t('settings.scaleConnected', {
            defaultValue: 'Connected — place weight on the scale.',
          })
        : res.error || 'Connection failed.',
      res.ok,
    );
  };

  const needsSerial = draft.protocol !== 'keyboard_wedge' && draft.brandPreset !== 'none';

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Scale className="w-5 h-5" />
            {t('settings.weighingScale', { defaultValue: 'Weighing Scale' })}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('settings.scaleHintWeb', {
              defaultValue:
                'Live weight in POS via USB/RS-232 (Chrome/Edge Web Serial or desktop app). Manual kg always available.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
              connStatus === 'connected'
                ? 'bg-green-50 text-green-700 border-green-200'
                : connStatus === 'connecting'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : connStatus === 'error'
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
            }`}
          >
            {connStatus === 'connected'
              ? 'Connected'
              : connStatus === 'connecting'
                ? 'Connecting…'
                : connStatus === 'error'
                  ? 'Error'
                  : 'Not connected'}
          </span>
          <span
            className={`text-xs px-2.5 py-1 rounded-md border font-medium ${
              isDirty
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-green-50 text-green-700 border-green-200'
            }`}
          >
            {isDirty ? 'Unsaved' : 'Saved'}
          </span>
        </div>
      </div>

      {toast && (
        <div
          className={`text-sm px-4 py-3 rounded-lg border ${
            toast.ok
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {!transport.available && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {transport.note}
        </p>
      )}

      {transport.available && (
        <p className="text-xs text-gray-500">
          Transport: {transport.mode === 'electron' ? 'Desktop serial' : 'Web Serial'} — {transport.note}
        </p>
      )}

      {/* Brand */}
      <section className="border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">
          {t('settings.scaleBrand', { defaultValue: 'Scale / brand' })}
        </h2>
        <label className="block">
          <span className="text-xs text-gray-500">Preset</span>
          <select
            className={`${SELECT_CLS} mt-1`}
            value={draft.brandPreset}
            onChange={(e) => applyBrandPreset(e.target.value as ScaleBrandPreset)}
          >
            {SCALE_BRAND_PRESETS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">{t('settings.scaleProtocol')}</span>
          <select
            className={`${SELECT_CLS} mt-1`}
            value={draft.protocol}
            onChange={(e) => updateDraft('protocol', e.target.value as ScaleProtocol)}
          >
            {SCALE_PROTOCOL_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        {draft.brandPreset === 'dibal' && (
          <p className="text-xs text-gray-500">
            Set the Dibal scale ECR interface to NCI or CAS, then match Protocol above.
          </p>
        )}
      </section>

      {/* Connection */}
      {needsSerial && (
        <section className="border border-gray-200 rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100">Connection</h2>
          {desktop ? (
            <label className="block">
              <span className="text-xs text-gray-500">{t('settings.scalePort')}</span>
              <select
                className={`${SELECT_CLS} mt-1 font-mono`}
                value={draft.path}
                onChange={(e) => updateDraft('path', e.target.value)}
              >
                <option value="">—</option>
                {ports.map((p) => (
                  <option key={p.id} value={p.path || p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="mt-2 text-xs text-gray-600 underline"
                onClick={() => void listPorts().then(setPorts)}
              >
                Refresh ports
              </button>
            </label>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Port:{' '}
                <span className="font-mono text-xs">
                  {draft.portId || 'Not granted yet'}
                </span>
              </p>
              <button
                type="button"
                disabled={!transport.available}
                onClick={async () => {
                  const p = await requestWebPort();
                  if (p) {
                    showToast('Serial port granted.', true);
                    void listPorts().then(setPorts);
                  }
                }}
                className="text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                {draft.portId ? 'Re-select port' : 'Grant serial port'}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs text-gray-500">{t('settings.scaleBaud')}</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.baudRate}
                onChange={(e) => updateDraft('baudRate', Number(e.target.value))}
              >
                {[4800, 9600, 19200, 38400, 115200].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">Data bits</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.dataBits}
                onChange={(e) => updateDraft('dataBits', Number(e.target.value) as 7 | 8)}
              >
                <option value={7}>7</option>
                <option value={8}>8</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">Parity</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.parity}
                onChange={(e) =>
                  updateDraft('parity', e.target.value as 'none' | 'even' | 'odd')
                }
              >
                <option value="none">None</option>
                <option value="even">Even</option>
                <option value="odd">Odd</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-gray-500">Stop bits</span>
              <select
                className={`${SELECT_CLS} mt-1`}
                value={draft.stopBits}
                onChange={(e) => updateDraft('stopBits', Number(e.target.value) as 1 | 2)}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
            </label>
          </div>
        </section>
      )}

      {/* Behaviour */}
      <section className="border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-medium text-gray-900 pb-2 border-b border-gray-100 mb-1">
          Behaviour
        </h2>
        {(
          [
            ['autoConnect', 'Auto-connect on weight entry', 'Open the scale when the POS weight modal opens'],
            ['stableOnly', 'Stable weight only', 'Ignore unstable / in-motion readings when the protocol reports status'],
          ] as const
        ).map(([key, label, sub]) => (
          <div
            key={key}
            className="flex items-center justify-between py-3 border-b border-gray-100 last:border-none"
          >
            <div>
              <p className="text-sm text-gray-800">{label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={draft[key]}
              onClick={() => updateDraft(key, !draft[key])}
              className={`relative w-10 h-6 rounded-full transition-colors ${
                draft[key] ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  draft[key] ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        ))}
      </section>

      {/* Test */}
      <section className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium text-gray-900">Test connection</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Live reading:{' '}
              <span className="font-mono text-gray-800">
                {lastWeightKg != null ? `${lastWeightKg.toFixed(3)} kg` : '—'}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={testing || !transport.available || draft.brandPreset === 'none'}
              onClick={() => void handleTest()}
              className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
            >
              {testing || connStatus === 'connecting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
                </>
              ) : (
                'Test connection'
              )}
            </button>
            <button
              type="button"
              onClick={async () => {
                await disconnect();
                setLastWeightKg(null);
              }}
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Disconnect
            </button>
          </div>
        </div>
        {errorMsg && <p className="text-xs text-red-600 mt-2">{errorMsg}</p>}
      </section>

      <p className="text-xs text-gray-400">
        Printer settings moved?{' '}
        <Link to="/settings?tab=printer" className="underline text-gray-600">
          Open Printer
        </Link>
      </p>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={resetDefaults}
          className="text-sm text-gray-500 hover:text-gray-800"
        >
          Reset to defaults
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={discardDraft}
            disabled={!isDirty}
            className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty}
            className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40"
          >
            Save settings
          </button>
        </div>
      </div>
    </div>
  );
}
